/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Shared constants + identity for the home-screen widgets (iOS + Android).
 *
 * Home-screen widgets run in a separate OS process that cannot read the app's
 * AsyncStorage / SecureStore. The bridge (`app/helpers/widgets/widgetBridge.js`)
 * therefore mirrors a compact "snapshot" into OS-shared storage:
 *   - iOS   → App Group `UserDefaults` (via `@bacons/apple-targets`).
 *   - Android → a well-known AsyncStorage key the widget's headless task reads.
 *
 * The iOS App Group id MUST match the one computed at build time in
 * `app.config.js` (`group.${iosPackageId || packageId}`), so both derive it from
 * the SAME variant manifest / runtime variant resolver.
 */

import { getAppVariant } from './appVariant';
import { INSIGHT_WIDGET_IDS } from './insightWidgets';

/** Storage key holding the JSON snapshot (also the App Group `UserDefaults` key). */
export const WIDGET_SNAPSHOT_KEY = 'HG_WIDGET_SNAPSHOT';

/**
 * App Group `UserDefaults` key holding ONLY the `insights` block of the snapshot (iOS). It is kept out of
 * `HG_WIDGET_SNAPSHOT` on purpose: every widget timeline parses the snapshot inside the extension's ~30 MB budget, and
 * the insights block (periods x modules x labels x catalog) is far larger than all the other sections together - the
 * core widgets that never read it were being killed (black faces) while merging it. Mirrors `kWidgetInsightsAppKey`
 * in `targets/widgets/InsightStore.swift`.
 */
export const WIDGET_INSIGHTS_KEY = 'HG_WIDGET_INSIGHTS';

/** App Group `UserDefaults` key mirroring the auth token (iOS direct-fetch future). */
export const WIDGET_TOKEN_KEY = 'HG_WIDGET_TOKEN';

/**
 * Layout version stamped on every published snapshot (`schemaVersion`). The iOS extension ignores a snapshot whose
 * version is HIGHER than the one it was built with (`kWidgetSnapshotSchemaVersion` in `targets/widgets/WidgetTimeline.swift`)
 * instead of half-decoding it; a missing field is a legacy snapshot and stays readable. Bump only on an INCOMPATIBLE
 * change of the snapshot layout, and bump the Swift constant with it.
 */
export const WIDGET_SNAPSHOT_VERSION = 2;

export {
    WIDGET_PREFS_KEY,
    WIDGET_REFRESH_MINUTES_KEY,
    WIDGET_REFRESH_OPTIONS,
    WIDGET_REFRESH_DEFAULT,
    normalizeRefreshMinutes,
} from './widgetRefreshConfig';

/** AsyncStorage key of the Android per-instance insight configs: `{ [widgetId]: { kind, config, updatedAt } }`. */
export const WIDGET_INSTANCE_CONFIG_KEY = 'HG_WIDGET_INSTANCE_CONFIG';

/**
 * Registered widget kinds. The `id` doubles as the iOS WidgetKit `kind` and the
 * `react-native-android-widget` widget name, so keep them in sync with the
 * native target (`targets/widgets/*.swift`) and the Android registrations
 * (`app.json` → `react-native-android-widget`).
 * @type {Array<{ id: string, titleKey: string }>}
 */
export const WIDGET_KINDS = [
    { id: 'UpcomingEvents', titleKey: 'widgets.upcomingEvents' },
    { id: 'Stats', titleKey: 'widgets.stats' },
    { id: 'Mail', titleKey: 'widgets.mail' },
    { id: 'QuickLaunch', titleKey: 'widgets.quickLaunch' },
    // Interactive widgets (iOS 17 AppIntents / Android clickAction).
    { id: 'TimeTracker', titleKey: 'widgets.timeTracker' },
    { id: 'PendingOrders', titleKey: 'widgets.pendingOrders' },
    // Quick entry point into the meeting recorder + its settings.
    { id: 'MeetingRecorder', titleKey: 'widgets.meetingRecorder' },
    // Team Chat: unread conversations; Meetings: live now + upcoming (Join).
    { id: 'TeamChat', titleKey: 'widgets.teamChat' },
    { id: 'Meetings', titleKey: 'widgets.meetings' },
    // VoIP: today's missed calls + recent contacts (tap to dial).
    { id: 'Calls', titleKey: 'widgets.calls' },
    // The caller's open assigned tasks with overdue / due-today counters.
    { id: 'MyTasks', titleKey: 'widgets.myTasks' },
    // Insight widgets (finance / bank / sales / operations / custom) — driven by
    // the preset registry in `./insightWidgets.js`; ids are shared with iOS.
    ...INSIGHT_WIDGET_IDS.map((id) => ({ id, titleKey: `widgets.${id.charAt(0).toLowerCase()}${id.slice(1)}` })),
];

/** Widget kind ids as a plain array (convenience for iterate/reload loops). */
export const WIDGET_NAMES = WIDGET_KINDS.map((kind) => kind.id);

/**
 * Resolve the iOS App Group id for the active build variant. Mirrors the
 * build-time computation in `app.config.js` so writes and reads land in the same
 * shared container across all five variants.
 * @returns {string} e.g. `group.hu.happysolutions.app`
 */
export const getWidgetAppGroup = () => {
    const variant = getAppVariant();
    const identity = variant.iosPackageId || variant.packageId;

    return `group.${identity}`;
};

/**
 * Deep-link URL scheme host used by widget taps. Routed by the app's linking
 * config (`app/config/linking` / navigator). Kept as one place so the native
 * widgets and the JS route map cannot drift.
 */
export const WIDGET_DEEPLINK_SCHEME = 'happyapp';

/**
 * Build a widget deep-link URL for a given target module/route.
 * @param {string} target One of `calendar` | `stats` | `mail` | `home` | a route name.
 * @param {Record<string, string>} [params] Optional query params.
 * @returns {string} e.g. `happyapp://widget/calendar`
 */
export const buildWidgetDeepLink = (target, params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');

    return `${WIDGET_DEEPLINK_SCHEME}://widget/${target}${query ? `?${query}` : ''}`;
};
